import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { cn } from "@/lib/utils";
import { AccountSettingsFieldSelect } from "@/views/Dashboard/Profile/components/AccountSettingsFieldSelect";
import { accountSettingsFieldTightControlClassName } from "@/views/Dashboard/Profile/components/SettingCard";
import { MarketDataInstrumentConsentField } from "@/components/MarketData/MarketDataInstrumentConsentField";
import { MarketDataSourceConnectPrompt } from "@/components/MarketData/MarketDataConnectGateNotice";
import { MarketDataBrokerReconnectDialog } from "@/components/MarketData/MarketDataBrokerReconnectDialog";
import { notifyTradesUpdated } from "@/hooks/useTradeStatistics";
import { resolveInstrumentForOhlcMenu } from "../../utils/instrumentResolveOhlcMenu";
import type { ResolveInstrumentResponse } from "../../types/instruments";
import type { BrokerAssetClassHints, InstrumentSetupRow, QuoteVariantCandidate } from "../../types/instrumentSetup";
import type { MarketDataSourceOption } from "../../types/marketDataSources";
import { DEFAULT_TRADED_ASSET_CLASSES } from "../../utils/brokerTradedAssetClasses";
import { formatAssetClassLabel } from "../../utils/tradingViewBroker";
import {
  formatMarketDataSourceOptionMenuLabel,
  hasSavedMarketDataSourcePreference,
  indexOfMatchingMarketDataSource,
  marketDataBrokerKey,
  resolveMarketDataSourcePickerSubtitle,
} from "../../utils/marketDataSourceChoice";
import { buildMarketDataSourceConnectAction } from "../../utils/marketDataConnectGate";
import { supportsInlineMarketDataBrokerReconnect, needsBrokerFeedReconnectConfirm } from "../../utils/marketDataBrokerReconnect";
import {
  instrumentSetupRowDataRevision,
  formatInstrumentSetupListingSummary,
  resolveInitialAssetClassForSetupRow,
} from "../../utils/instrumentSetupManageRow";
import { humanizeApiUserCopy } from "../../utils/humanizeApiUserCopy";
import { toastError } from "../../utils/showApiErrorToast";
import {
  INSTRUMENT_AUTO_MAP_STAGE_BODY,
  INSTRUMENT_AUTO_MAP_STAGE_TITLE,
  INSTRUMENT_MD_UNSUPPORTED_AFTER_SKIP_BODY,
  instrumentSetupResolutionWarningBody,
  instrumentSetupResolutionWarningTitle,
  isInstrumentQuoteAmbiguityWarning,
  isInstrumentRegistryMappingWarning,
} from "../../utils/instrumentAutoMapCopy";
import {
  inferAttributionFromSource,
  requiresMarketDataConsentForSetup,
  resolveLayer2ConsentLabel,
} from "../../utils/marketDataAttributionCopy";
import {
  buildInstrumentResolveRequest,
  describeEmptyOhlcSourcesForQuoteVariant,
  describeInstrumentSetupSaveBlockers,
  findQuoteVariantCandidate,
  isQuoteVariantPickerRequired,
  needsInstrumentSetupInitialConfirm,
  needsOhlcLaneRefresh,
  normalizeQuoteVariantCandidates,
  preferredSourceForActiveQuoteVariant,
  quoteVariantSelectOptions,
  marketDataSourcesForQuoteVariantEditor,
  resolveInitialQuoteVariant,
  resolvedSymbolForInstrumentSave,
  seedQuoteVariantHintForRow,
  showQuoteVariantPickerForRow,
  sourceIndexForActiveQuoteVariant,
  type InstrumentRowEditorSelection,
} from "../../utils/instrumentSetupSave";
import {
  BROKER_ALERT_CLASS,
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
  BROKER_FIELD_DESC_CLASS,
  BrokerAlertIcon,
} from "../Broker/brokerAlertUi";

function assetClassOptions(
  row: InstrumentSetupRow,
  hints?: BrokerAssetClassHints
): string[] {
  const fromHints = hints?.allowed_asset_classes
    ?.map((v) => v.trim().toLowerCase())
    .filter(Boolean);
  if (fromHints && fromHints.length > 0) return Array.from(new Set(fromHints));
  if (row.valid_asset_classes?.length) {
    return Array.from(
      new Set(
        row.valid_asset_classes
          .map((v) => v.trim().toLowerCase())
          .filter(Boolean)
      )
    );
  }
  return [...DEFAULT_TRADED_ASSET_CLASSES];
}

function sourceFingerprint(source?: MarketDataSourceOption | null): string {
  if (!source) return "";
  return [
    source.option_id?.trim() || "",
    source.md_listing_key?.trim() || "",
    source.market_data_broker ?? source.broker ?? "",
    source.venue ?? source.venue_label ?? "",
    source.ibkr_conid != null ? String(source.ibkr_conid) : "",
    source.binance_product ?? "",
  ].join("|");
}

function selectionFingerprint(input: {
  assetClass: string;
  resolvedSymbol?: string;
  source?: MarketDataSourceOption | null;
  consentGranted: boolean;
}): string {
  return [
    input.assetClass.trim().toLowerCase(),
    input.resolvedSymbol?.trim() || "",
    sourceFingerprint(input.source),
    input.consentGranted ? "1" : "0",
  ].join("::");
}

function buildBaseline(input: {
  assetClass: string;
  resolvedSymbol: string;
  sources: MarketDataSourceOption[];
  sourceIndex: number;
  consentGranted: boolean;
  fixedAssetClass?: string;
}): string {
  return selectionFingerprint({
    assetClass: input.assetClass || input.fixedAssetClass || "",
    resolvedSymbol: input.resolvedSymbol || undefined,
    source: input.sources[input.sourceIndex] ?? input.sources[0] ?? null,
    consentGranted: input.consentGranted,
  });
}

type OhlcDisplaySnapshot = {
  sources: MarketDataSourceOption[];
  selectedIndex: string;
};

function ohlcDisplayCacheKey(
  brokerAccountCode: string,
  symbol: string,
  quote: string
): string {
  return `${brokerAccountCode}:${symbol}:${quote}`.toUpperCase();
}

function rememberOhlcDisplayCache(
  cache: Map<string, OhlcDisplaySnapshot>,
  key: string,
  snapshot: OhlcDisplaySnapshot
): void {
  if (snapshot.sources.length === 0) return;
  cache.set(key, snapshot);
}

function resolveOhlcDisplaySnapshot(input: {
  primary: OhlcDisplaySnapshot;
  cache: Map<string, OhlcDisplaySnapshot>;
  cacheKey: string;
}): OhlcDisplaySnapshot {
  if (input.primary.sources.length > 0) {
    rememberOhlcDisplayCache(input.cache, input.cacheKey, input.primary);
    return input.primary;
  }
  const cached = input.cache.get(input.cacheKey);
  if (cached && cached.sources.length > 0) return cached;
  return input.primary;
}

/** Avoid flashing spinners when resolve finishes in under ~300ms. */
function useDelayedTrue(value: boolean, delayMs = 300): boolean {
  const [delayed, setDelayed] = useState(false);
  useEffect(() => {
    if (!value) {
      setDelayed(false);
      return;
    }
    const timer = window.setTimeout(() => setDelayed(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);
  return delayed;
}

function buildOhlcDisplaySnapshot(input: {
  row: InstrumentSetupRow;
  resolveResult?: ResolveInstrumentResponse | null;
  quote: string;
  quoteCandidates: QuoteVariantCandidate[];
  allowStaleLaneFallback?: boolean;
}): OhlcDisplaySnapshot {
  const quote = input.quote.trim();
  const sources = marketDataSourcesForQuoteVariantEditor(
    input.resolveResult,
    input.row,
    quote || undefined,
    { allowStaleLaneFallback: input.allowStaleLaneFallback }
  );
  const index = sourceIndexForActiveQuoteVariant({
    row: input.row,
    resolveResult: input.resolveResult,
    activeResolvedSymbol: quote || undefined,
    quoteCandidates: input.quoteCandidates,
  });
  return {
    sources,
    selectedIndex: String(Math.max(0, index)),
  };
}

export type InstrumentRowEditorFooterActions = {
  symbol: string;
  canConfirm: boolean;
  confirmLabel: string;
  confirmHint: string | null;
  /** True while resolve-before-save or parent save is in flight. */
  busy: boolean;
  /** Primary confirm — submit Asset class / OHLC selection. */
  onConfirm: () => void;
  /** Per-symbol skip (not the same as leaving the wizard). */
  skipLabel: string | null;
  onSkip: (() => void) | null;
};

export function InstrumentMarketDataRowEditor(props: {
  row: InstrumentSetupRow;
  brokerAccountCode: string;
  assetClassHints?: BrokerAssetClassHints;
  saving?: boolean;
  compactHeader?: boolean;
  /** Prefetched resolve — editor mounts already hydrated (no default flash). */
  initialResolveResult?: ResolveInstrumentResponse | null;
  /** Always show OHLC source (manage / reconfigure UI). */
  alwaysShowSourcePicker?: boolean;
  /** Confirm only after the user changes something vs the loaded preference. */
  requireDirtyToSave?: boolean;
  /**
   * Where Confirm / skip controls render.
   * Wizard uses `footer` so submit sits next to Continue later.
   */
  actionsPlacement?: "inline" | "footer";
  /** Called when footer actions change (only when actionsPlacement is footer). */
  onFooterActionsChange?: (actions: InstrumentRowEditorFooterActions | null) => void;
  /**
   * Skip / continue without charts (unmappable or user declines OHLC).
   * When omitted, skip control is hidden.
   */
  onSkip?: (reason: "unsupported" | "user_declined") => void | Promise<void>;
  onSave: (
    selection: InstrumentRowEditorSelection,
    consentGranted: boolean,
    resolveContext?: Pick<
      ResolveInstrumentResponse,
      "md_listing_key" | "instrument_id" | "asset_class"
    >
  ) => void | Promise<void | boolean>;
  /** Reload instrument-setup after inline broker reconnect + OHLC save. */
  onSetupRefresh?: () => Promise<void>;
  /** Nested reconnect dialog — parent wizard should drop modal trap while open. */
  onNestedDialogOpenChange?: (open: boolean) => void;
}): React.ReactElement {
  const history = useHistory();
  const fixedAssetClasses = useMemo(
    () => props.assetClassHints?.fixed_values ?? [],
    [props.assetClassHints?.fixed_values]
  );
  const hideAssetClassPicker = fixedAssetClasses.length > 0;
  const requireDirtyToSave = props.requireDirtyToSave === true;
  const alwaysShowSourcePicker = props.alwaysShowSourcePicker === true;

  const row = props.row;
  const rowDataRevision = useMemo(() => instrumentSetupRowDataRevision(row), [row]);
  const resolveSeed = props.initialResolveResult ?? null;

  const quoteCandidates = useMemo(
    () => normalizeQuoteVariantCandidates(row.quote_variant_candidates),
    [row.quote_variant_candidates]
  );
  const quoteVariantOptions = useMemo(
    () => quoteVariantSelectOptions(quoteCandidates),
    [quoteCandidates]
  );

  const seedAssetClass =
    resolveInitialAssetClassForSetupRow(row, props.assetClassHints) ||
    resolveSeed?.suggested_asset_class?.trim().toLowerCase() ||
    resolveSeed?.asset_class?.trim().toLowerCase() ||
    "";
  const seedResolvedSymbol = resolveInitialQuoteVariant(
    seedQuoteVariantHintForRow(row),
    quoteCandidates,
    row.auto_resolved_quote_variant
  );
  const showQuoteVariantPicker = showQuoteVariantPickerForRow(row);
  const quoteVariantFieldLabel =
    row.requires_quote_variant_choice && !row.quote_variant_overridable
      ? "Quote / perp variant"
      : "Change quote variant";

  const [assetClass, setAssetClass] = useState(seedAssetClass);
  const [resolvedSymbol, setResolvedSymbol] = useState(seedResolvedSymbol);
  /** Quote variant the OHLC menu was built for — updates only after resolve completes. */
  const [ohlcLaneQuote, setOhlcLaneQuote] = useState(seedResolvedSymbol);
  const [ohlcSnapshot, setOhlcSnapshot] = useState<OhlcDisplaySnapshot>(() =>
    buildOhlcDisplaySnapshot({
      row,
      resolveResult: resolveSeed,
      quote: seedResolvedSymbol,
      quoteCandidates,
    })
  );
  const [localResolve, setLocalResolve] = useState<ResolveInstrumentResponse | null>(null);
  const [resolving, setResolving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reconnectDialogOpen, setReconnectDialogOpenState] = useState(false);
  const [recentlySaved, setRecentlySaved] = useState(false);

  const setReconnectDialogOpen = useCallback(
    (open: boolean) => {
      props.onNestedDialogOpenChange?.(open);
      setReconnectDialogOpenState(open);
    },
    [props.onNestedDialogOpenChange]
  );

  React.useEffect(() => {
    return () => {
      props.onNestedDialogOpenChange?.(false);
    };
  }, [props.onNestedDialogOpenChange]);

  const activeResolve = localResolve ?? resolveSeed;

  const sources = useMemo(
    () =>
      marketDataSourcesForQuoteVariantEditor(
        activeResolve,
        row,
        ohlcLaneQuote.trim() || undefined,
        { allowStaleLaneFallback: !activeResolve }
      ),
    [
      activeResolve,
      ohlcLaneQuote,
      row.symbol,
      row.market_data_sources,
      row.candidate_brokers,
      row.md_listing_key,
      row.market_data_broker,
      row.quote_variant_candidates,
    ]
  );

  const seedSourceIndex = sourceIndexForActiveQuoteVariant({
    row,
    resolveResult: activeResolve ?? resolveSeed,
    activeResolvedSymbol: seedResolvedSymbol,
    quoteCandidates,
  });
  const seedSource = sources[seedSourceIndex] ?? sources[0];
  const seedConsentGranted = !requiresMarketDataConsentForSetup({
    row,
    source: seedSource,
  });

  const [consentGranted, setConsentGranted] = useState(seedConsentGranted);
  const [selectedSourceIndex, setSelectedSourceIndex] = useState(String(seedSourceIndex));
  const baselineFingerprintRef = useRef(
    buildBaseline({
      assetClass: seedAssetClass,
      resolvedSymbol: seedResolvedSymbol,
      sources,
      sourceIndex: seedSourceIndex,
      consentGranted: seedConsentGranted,
      fixedAssetClass: fixedAssetClasses[0],
    })
  );
  const hydratedSymbolRef = useRef<string | null>(null);
  const hydrateSymbolKeyRef = useRef(`${props.brokerAccountCode}:${row.symbol}`);
  const ohlcDisplayCacheRef = useRef(new Map<string, OhlcDisplaySnapshot>());
  const prefetchResolveKeyRef = useRef<string | null>(null);
  const variantResolveSeqRef = useRef(0);
  const skipNextAssetClassResolveRef = useRef(false);
  /** Asset class + quote at last successful resolve — asset-class edits resolve only when these change. */
  const baselineEditRef = useRef({ assetClass: seedAssetClass, resolvedSymbol: seedResolvedSymbol });
  const selectedSourceRef = useRef<MarketDataSourceOption | null>(null);
  const rowRef = useRef(row);
  rowRef.current = row;

  const hydrateKey = `${props.brokerAccountCode}:${row.symbol}:${rowDataRevision}`;
  // null until first hydrate — if we seed this to hydrateKey, the layout effect
  // short-circuits and never POST /resolve for the suggested quote variant.
  const [syncedHydrateKey, setSyncedHydrateKey] = useState<string | null>(null);
  const rowSynced = syncedHydrateKey === hydrateKey;

  const rowSeedSnapshot = useMemo(
    () =>
      buildOhlcDisplaySnapshot({
        row,
        resolveResult: resolveSeed,
        quote: seedResolvedSymbol,
        quoteCandidates,
        allowStaleLaneFallback: true,
      }),
    [quoteCandidates, resolveSeed, row, seedResolvedSymbol]
  );

  const ohlcCacheKey = ohlcDisplayCacheKey(
    props.brokerAccountCode,
    row.symbol,
    (rowSynced ? resolvedSymbol : seedResolvedSymbol).trim()
  );

  const showResolvingUi = useDelayedTrue(resolving);
  /** Keep last OHLC menu frozen while resolve runs; visuals delay separately. */
  const ohlcDisplayFrozen = rowSynced && resolving;
  const ohlcVisualPending = rowSynced && showResolvingUi;

  const effectiveAssetClass = rowSynced ? assetClass : seedAssetClass;
  const effectiveResolvedSymbol = rowSynced ? resolvedSymbol : seedResolvedSymbol;
  const effectiveOhlcSnapshot = rowSynced ? ohlcSnapshot : rowSeedSnapshot;
  const effectiveSelectedSourceIndex = rowSynced
    ? selectedSourceIndex
    : rowSeedSnapshot.selectedIndex;

  const ohlcLaneNeedsRefresh = useMemo(
    () =>
      needsOhlcLaneRefresh({
        resolveResult: activeResolve,
        row,
        activeResolvedSymbol: effectiveResolvedSymbol.trim() || undefined,
        quoteCandidates,
      }),
    [activeResolve, effectiveResolvedSymbol, quoteCandidates, row]
  );

  const ohlcAwaitingFreshLanes = !rowSynced || resolving || ohlcLaneNeedsRefresh;

  const assetClassMenuOptions = useMemo(() => {
    const options = assetClassOptions(row, props.assetClassHints);
    const current = assetClass.trim().toLowerCase();
    const merged =
      current && !options.includes(current) ? [current, ...options] : options;
    return merged.map((o) => ({ value: o, label: formatAssetClassLabel(o) }));
  }, [assetClass, props.assetClassHints, row.valid_asset_classes]);

  const liveSources = rowSynced ? sources : rowSeedSnapshot.sources;

  const resolvedDisplayOhlc = useMemo(() => {
    const primary: OhlcDisplaySnapshot = ohlcDisplayFrozen
      ? effectiveOhlcSnapshot
      : {
          sources: liveSources,
          selectedIndex: effectiveSelectedSourceIndex,
        };
    return resolveOhlcDisplaySnapshot({
      primary,
      cache: ohlcDisplayCacheRef.current,
      cacheKey: ohlcCacheKey,
    });
  }, [
    effectiveOhlcSnapshot,
    effectiveSelectedSourceIndex,
    liveSources,
    ohlcCacheKey,
    ohlcDisplayFrozen,
  ]);

  const sourceMenuOptions = useMemo(() => {
    return resolvedDisplayOhlc.sources.map((source, index) => ({
      value: String(index),
      label: formatMarketDataSourceOptionMenuLabel(source),
    }));
  }, [resolvedDisplayOhlc.sources]);

  const displaySelectedIndex = useMemo(() => {
    const max = Math.max(0, resolvedDisplayOhlc.sources.length - 1);
    const parsed = Number(resolvedDisplayOhlc.selectedIndex);
    const clamped = Number.isFinite(parsed)
      ? Math.min(Math.max(0, parsed), max)
      : 0;
    return String(clamped);
  }, [resolvedDisplayOhlc.selectedIndex, resolvedDisplayOhlc.sources.length]);
  const displaySources = resolvedDisplayOhlc.sources;

  const effectiveAssetClassValue = useCallback((): string => {
    return (hideAssetClassPicker ? fixedAssetClasses[0] ?? "" : assetClass).trim();
  }, [assetClass, fixedAssetClasses, hideAssetClassPicker]);

  const applyQuoteVariantResolveResult = useCallback(
    (result: ResolveInstrumentResponse, quoteSelectValue: string) => {
      setLocalResolve(result);
      const latestRow = rowRef.current;
      const quote = quoteSelectValue.trim();
      const nextSources = marketDataSourcesForQuoteVariantEditor(
        result,
        latestRow,
        quote || undefined
      );
      if (nextSources.length > 0) {
        const nextIndex = sourceIndexForActiveQuoteVariant({
          row: latestRow,
          resolveResult: result,
          activeResolvedSymbol: quote || undefined,
          quoteCandidates,
        });
        const selectedIndex = String(Math.max(0, nextIndex));
        setSelectedSourceIndex(selectedIndex);
        setOhlcSnapshot({
          sources: nextSources,
          selectedIndex,
        });
        rememberOhlcDisplayCache(
          ohlcDisplayCacheRef.current,
          ohlcDisplayCacheKey(
            props.brokerAccountCode,
            latestRow.symbol,
            quote
          ),
          { sources: nextSources, selectedIndex }
        );
      }
      baselineEditRef.current = {
        assetClass: effectiveAssetClassValue(),
        resolvedSymbol: quote,
      };
      setOhlcLaneQuote(quote);
    },
    [effectiveAssetClassValue, props.brokerAccountCode, quoteCandidates]
  );

  /** POST /resolve — always includes resolved_symbol when the quote picker is shown. */
  const runQuoteVariantResolve = useCallback(
    async (
      quoteSelectValue: string,
      options?: { assetClassOverride?: string; force?: boolean }
    ) => {
      const latestRow = rowRef.current;
      const tradeSymbol = latestRow.symbol.trim();
      const effectiveAsset = (options?.assetClassOverride ?? effectiveAssetClassValue()).trim();
      const quote = quoteSelectValue.trim();
      const needsQuote = showQuoteVariantPickerForRow(latestRow);
      if (!tradeSymbol || !props.brokerAccountCode.trim() || !effectiveAsset) return null;
      if (needsQuote && !quote) return null;

      const resolvedForRequest = needsQuote ? quote : quote || undefined;
      const dedupeKey = `${props.brokerAccountCode}:${tradeSymbol}:${effectiveAsset}:${resolvedForRequest ?? ""}`;
      if (!options?.force && prefetchResolveKeyRef.current === dedupeKey) return null;
      prefetchResolveKeyRef.current = dedupeKey;

      const resolveBody = buildInstrumentResolveRequest({
        tradeSymbol,
        assetClass: effectiveAsset,
        brokerAccountCode: props.brokerAccountCode,
        resolvedSymbol: resolvedForRequest,
        quoteCandidates,
      });

      const seq = ++variantResolveSeqRef.current;
      setResolving(true);
      try {
        const result = await resolveInstrumentForOhlcMenu(resolveBody);
        if (seq !== variantResolveSeqRef.current) return null;
        applyQuoteVariantResolveResult(result, quote);
        return result;
      } catch {
        if (seq === variantResolveSeqRef.current && prefetchResolveKeyRef.current === dedupeKey) {
          prefetchResolveKeyRef.current = null;
        }
        if (seq === variantResolveSeqRef.current) {
          setLocalResolve(null);
        }
        return null;
      } finally {
        if (seq === variantResolveSeqRef.current) {
          setResolving(false);
        }
      }
    },
    [applyQuoteVariantResolveResult, effectiveAssetClassValue, props.brokerAccountCode, quoteCandidates]
  );

  const handleQuoteVariantChange = useCallback(
    (value: string) => {
      const next = value.trim();
      if (!next) return;
      setResolvedSymbol(next);
      prefetchResolveKeyRef.current = null;
      void runQuoteVariantResolve(next, { force: true });
    },
    [runQuoteVariantResolve]
  );

  // Re-hydrate when symbol/account changes or BE row market-data state updates (e.g. inline reconnect).
  useLayoutEffect(() => {
    if (syncedHydrateKey === hydrateKey) return;
    hydratedSymbolRef.current = hydrateKey;
    prefetchResolveKeyRef.current = null;
    variantResolveSeqRef.current += 1;
    skipNextAssetClassResolveRef.current = true;
    setResolving(false);
    setReconnectDialogOpen(false);

    const nextSnapshot = buildOhlcDisplaySnapshot({
      row,
      resolveResult: resolveSeed,
      quote: seedResolvedSymbol,
      quoteCandidates,
      allowStaleLaneFallback: true,
    });
    rememberOhlcDisplayCache(
      ohlcDisplayCacheRef.current,
      ohlcDisplayCacheKey(props.brokerAccountCode, row.symbol, seedResolvedSymbol),
      nextSnapshot
    );
    const nextSource = nextSnapshot.sources[Number(nextSnapshot.selectedIndex)] ?? nextSnapshot.sources[0];
    const nextConsentGranted = !requiresMarketDataConsentForSetup({
      row,
      source: nextSource,
    });

    setOhlcSnapshot(nextSnapshot);
    setLocalResolve(null);
    setAssetClass(seedAssetClass);
    setResolvedSymbol(seedResolvedSymbol);
    setOhlcLaneQuote(seedResolvedSymbol);
    setConsentGranted(nextConsentGranted);
    setSelectedSourceIndex(nextSnapshot.selectedIndex);
    const hydrateSymbolKey = `${props.brokerAccountCode}:${row.symbol}`;
    if (hydrateSymbolKeyRef.current !== hydrateSymbolKey) {
      setRecentlySaved(false);
      hydrateSymbolKeyRef.current = hydrateSymbolKey;
    }
    baselineEditRef.current = {
      assetClass: seedAssetClass,
      resolvedSymbol: seedResolvedSymbol,
    };
    baselineFingerprintRef.current = buildBaseline({
      assetClass: seedAssetClass,
      resolvedSymbol: seedResolvedSymbol,
      sources: nextSnapshot.sources,
      sourceIndex: Number(nextSnapshot.selectedIndex),
      consentGranted: nextConsentGranted,
      fixedAssetClass: fixedAssetClasses[0],
    });

    // Account scan supplies quote variants; OHLC source menus come from resolve.
    // If this active row already has a suggested/selected variant but no sources yet,
    // resolve once for this symbol only — not once-per-symbol for the whole account.
    const seedAsset = (
      hideAssetClassPicker ? fixedAssetClasses[0] ?? "" : seedAssetClass
    ).trim();
    if (
      nextSnapshot.sources.length === 0 &&
      seedResolvedSymbol.trim() &&
      seedAsset &&
      props.brokerAccountCode.trim() &&
      row.symbol.trim() &&
      (alwaysShowSourcePicker || showQuoteVariantPicker)
    ) {
      void runQuoteVariantResolve(seedResolvedSymbol, { assetClassOverride: seedAsset });
    }

    setSyncedHydrateKey(hydrateKey);
  }, [
    alwaysShowSourcePicker,
    fixedAssetClasses,
    hideAssetClassPicker,
    hydrateKey,
    props.brokerAccountCode,
    quoteCandidates,
    row,
    row.symbol,
    rowDataRevision,
    resolveSeed,
    runQuoteVariantResolve,
    seedAssetClass,
    seedResolvedSymbol,
    showQuoteVariantPicker,
    syncedHydrateKey,
  ]);

  // Asset-class edits re-resolve with the current quote variant (quote picks resolve immediately).
  useEffect(() => {
    if (skipNextAssetClassResolveRef.current) {
      skipNextAssetClassResolveRef.current = false;
      return;
    }
    const effectiveAsset = effectiveAssetClassValue();
    const effectiveQuote = resolvedSymbol.trim();
    if (effectiveAsset === baselineEditRef.current.assetClass.trim()) {
      return;
    }
    if (!effectiveAsset || !props.brokerAccountCode.trim() || !row.symbol.trim()) return;
    if (showQuoteVariantPicker && !effectiveQuote) return;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      void runQuoteVariantResolve(effectiveQuote, {
        assetClassOverride: effectiveAsset,
        force: true,
      });
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    assetClass,
    effectiveAssetClassValue,
    props.brokerAccountCode,
    resolvedSymbol,
    row.symbol,
    runQuoteVariantResolve,
    showQuoteVariantPicker,
  ]);

  const selectedSource = useMemo(() => {
    if (rowSynced) {
      return sources[Number(selectedSourceIndex)] ?? sources[0];
    }
    return displaySources[Number(displaySelectedIndex)] ?? displaySources[0];
  }, [
    displaySelectedIndex,
    displaySources,
    rowSynced,
    selectedSourceIndex,
    sources,
  ]);
  selectedSourceRef.current = selectedSource ?? null;

  const selectedSourceConnectAction = useMemo(
    () => buildMarketDataSourceConnectAction(selectedSource),
    [selectedSource]
  );

  const selectedBroker = marketDataBrokerKey(selectedSource ?? {});
  const needsBrokerReconnectConfirm = useMemo(
    () =>
      needsBrokerFeedReconnectConfirm({
        row,
        source: selectedSource,
        connectAction: selectedSourceConnectAction,
      }),
    [row, selectedSource, selectedSourceConnectAction]
  );
  const confirmActionLabel = needsBrokerReconnectConfirm
    ? `Connect & confirm ${row.symbol}`
    : `Confirm ${row.symbol}`;

  const needsConsent = useMemo(() => {
    if (needsBrokerReconnectConfirm || selectedSourceConnectAction) return false;
    return requiresMarketDataConsentForSetup({
      row,
      source: selectedSource,
    });
  }, [needsBrokerReconnectConfirm, row, selectedSource, selectedSourceConnectAction]);

  const consentAttribution = useMemo(
    () => inferAttributionFromSource(selectedSource) ?? row.market_data_attribution,
    [row.market_data_attribution, selectedSource]
  );

  useEffect(() => {
    if (!needsConsent) {
      setConsentGranted(true);
    }
  }, [needsConsent]);

  const consentSourceKey =
    selectedSource?.option_id ?? selectedSource?.md_listing_key ?? selectedSourceIndex;
  const prevConsentSourceKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (prevConsentSourceKeyRef.current === consentSourceKey) return;
    const isInitial = prevConsentSourceKeyRef.current === null;
    prevConsentSourceKeyRef.current = consentSourceKey;
    if (!isInitial && needsConsent) {
      setConsentGranted(false);
    }
  }, [consentSourceKey, needsConsent]);

  const selectedSourceSubtitle = useMemo(() => {
    if (!selectedSource) return null;
    return resolveMarketDataSourcePickerSubtitle(selectedSource);
  }, [selectedSource]);
  const showOhlcLaneEmpty =
    displaySources.length === 0 && !ohlcAwaitingFreshLanes && !showResolvingUi;
  const showSourcePicker =
    alwaysShowSourcePicker ||
    row.quote_variant_overridable ||
    ohlcLaneNeedsRefresh ||
    row.requires_market_data_source_choice ||
    row.requires_market_data_broker_choice ||
    activeResolve?.requires_market_data_source_choice ||
    displaySources.length > 1 ||
    (ohlcDisplayFrozen && displaySources.length > 0);

  const needsInitialSave = useMemo(
    () => needsInstrumentSetupInitialConfirm(row, selectedSource),
    [row, selectedSource]
  );
  const effectiveRequireDirtyToSave = requireDirtyToSave && !needsInitialSave;

  const selection: InstrumentRowEditorSelection = {
    assetClass: hideAssetClassPicker ? fixedAssetClasses[0] : assetClass.trim(),
    resolvedSymbol: resolvedSymbol.trim() || undefined,
    marketDataSource: selectedSource,
    marketDataBroker:
      selectedSource?.market_data_broker ??
      selectedSource?.broker ??
      activeResolve?.market_data_broker,
  };

  const currentFingerprint = selectionFingerprint({
    assetClass: selection.assetClass,
    resolvedSymbol: selection.resolvedSymbol,
    source: selectedSource,
    consentGranted,
  });
  const isDirty =
    !effectiveRequireDirtyToSave ||
    currentFingerprint !== baselineFingerprintRef.current;

  const selectedQuoteCandidate = useMemo(
    () => findQuoteVariantCandidate(quoteCandidates, effectiveResolvedSymbol),
    [effectiveResolvedSymbol, quoteCandidates]
  );

  const saveBlocker =
    describeInstrumentSetupSaveBlockers({
      row,
      selection,
      consentGranted,
    }) ??
    (showSourcePicker && showOhlcLaneEmpty && ohlcLaneNeedsRefresh
      ? "OHLC sources for the selected quote variant are not available yet."
      : null);
  const saving = props.saving;
  const preferenceSavedAndCurrent =
    hasSavedMarketDataSourcePreference(row) &&
    !isDirty &&
    saveBlocker === null &&
    !needsInitialSave &&
    !confirming &&
    !saving;
  const showPreferenceSavedState = preferenceSavedAndCurrent || recentlySaved;
  const savedListingSummary = formatInstrumentSetupListingSummary(row);
  const canSave = saveBlocker === null && isDirty && !resolving && !showPreferenceSavedState;
  const mdUnsupported =
    row.md_supported === false || activeResolve?.md_supported === false;
  const showSkip = typeof props.onSkip === "function";
  const registryWarnings = (row.resolution_warnings ?? []).filter((w) =>
    isInstrumentRegistryMappingWarning(w)
  );
  const otherWarnings = (row.resolution_warnings ?? []).filter((w) => {
    if (isInstrumentRegistryMappingWarning(w)) return false;
    // Quote-variant picker is the fix — skip redundant report-era BE copy.
    if (showQuoteVariantPicker && isInstrumentQuoteAmbiguityWarning(w)) return false;
    return true;
  });
  const needsTesseraAutoMapStage =
    Boolean(row.requires_asset_class_confirmation) ||
    mdUnsupported ||
    registryWarnings.length > 0;
  const onSave = props.onSave;
  const onSkipProp = props.onSkip;
  const onFooterActionsChange = props.onFooterActionsChange;

  /** Prefer Confirm so choosing Asset class can trigger Tessera's locate/map attempt. */
  const canAttemptAutoMap = needsTesseraAutoMapStage && !hideAssetClassPicker;
  const actionsInFooter = props.actionsPlacement === "footer";
  const primaryIsSkipOnly = mdUnsupported && showSkip && !canAttemptAutoMap;
  const showSecondarySkip =
    showSkip && !primaryIsSkipOnly && (canAttemptAutoMap || !mdUnsupported);
  const secondarySkipReason: "unsupported" | "user_declined" = mdUnsupported
    ? "unsupported"
    : "user_declined";

  const handleConfirm = useCallback(() => {
    if (!canSave || confirming) return;

    if (needsBrokerReconnectConfirm) {
      setReconnectDialogOpen(true);
      return;
    }

    void (async () => {
      const chosenAssetClass = selection.assetClass.trim();
      if (!chosenAssetClass) return;

      setConfirming(true);
      try {
        if (isQuoteVariantPickerRequired(row) && !selection.resolvedSymbol?.trim()) {
          toastError("Choose a quote or perp variant before confirming.");
          return;
        }

        const result = await resolveInstrumentForOhlcMenu(
          buildInstrumentResolveRequest({
            tradeSymbol: row.symbol,
            assetClass: chosenAssetClass,
            brokerAccountCode: props.brokerAccountCode,
            resolvedSymbol: selection.resolvedSymbol ?? resolvedSymbol,
            quoteCandidates,
          })
        );
        setLocalResolve(result);

        const nextSources = marketDataSourcesForQuoteVariantEditor(
          result,
          row,
          selection.resolvedSymbol ?? resolvedSymbol
        );
        let source = selection.marketDataSource;
        if (nextSources.length > 0) {
          const preferred = preferredSourceForActiveQuoteVariant({
            row,
            resolveResult: result,
            activeResolvedSymbol: selection.resolvedSymbol ?? resolvedSymbol,
            quoteCandidates,
          });
          let nextIndex = sourceIndexForActiveQuoteVariant({
            row,
            resolveResult: result,
            activeResolvedSymbol: selection.resolvedSymbol ?? resolvedSymbol,
            quoteCandidates,
          });
          if (source) {
            const matchedIdx = indexOfMatchingMarketDataSource(nextSources, source);
            if (matchedIdx >= 0) {
              source = nextSources[matchedIdx];
              nextIndex = matchedIdx;
            } else if (preferred) {
              const preferredIdx = indexOfMatchingMarketDataSource(nextSources, preferred);
              if (preferredIdx >= 0) {
                source = nextSources[preferredIdx];
                nextIndex = preferredIdx;
              } else {
                source = nextSources[nextIndex] ?? nextSources[0];
              }
            } else {
              source = nextSources[nextIndex] ?? nextSources[0];
            }
          } else {
            source = preferred ?? nextSources[nextIndex] ?? nextSources[0];
          }
          const selectedIndex = String(Math.max(0, nextIndex));
          setSelectedSourceIndex(selectedIndex);
          setOhlcSnapshot({ sources: nextSources, selectedIndex });
        }

        const proxySymbol = resolvedSymbolForInstrumentSave({
          selectionResolvedSymbol: selection.resolvedSymbol ?? resolvedSymbol,
          resolveSuggestedQuoteVariant: result.suggested_quote_variant,
          suggestedQuoteVariant: row.suggested_quote_variant,
          autoResolvedQuoteVariant:
            result.auto_resolved_quote_variant ?? row.auto_resolved_quote_variant,
          quoteCandidates,
        });
        if (proxySymbol) {
          const match = findQuoteVariantCandidate(quoteCandidates, proxySymbol);
          setResolvedSymbol(match ? match.symbol.trim() : proxySymbol);
        }

        const marketDataBroker =
          source?.market_data_broker ??
          source?.broker ??
          result.market_data_broker ??
          selection.marketDataBroker;
        const instrumentId =
          result.instrument_id ??
          selectedQuoteCandidate?.instrument_id ??
          row.instrument_id;

        if (!marketDataBroker && !source?.md_listing_key && !instrumentId && !source) {
          toastError(`Could not map ${row.symbol}`, {
            description:
              "Tessera couldn't locate this symbol after your Asset class choice. Skip charts for now, or reach out to us and we'll look into it.",
          });
          return;
        }

        const saveResult = await onSave(
          {
            ...selection,
            assetClass: chosenAssetClass,
            resolvedSymbol: proxySymbol,
            marketDataSource: source,
            marketDataBroker: marketDataBroker?.trim() || undefined,
          },
          consentGranted,
          {
            instrument_id: instrumentId,
            asset_class: chosenAssetClass,
          }
        );
        if (saveResult === false) return;

        const savedSource = source ?? selectedSource;
        baselineFingerprintRef.current = selectionFingerprint({
          assetClass: chosenAssetClass,
          resolvedSymbol: proxySymbol,
          source: savedSource,
          consentGranted,
        });
        setRecentlySaved(true);
      } catch (error) {
        const message =
          error instanceof Error && error.message.trim()
            ? humanizeApiUserCopy(error.message)
            : `Could not map ${row.symbol}.`;
        toastError(`Could not save ${row.symbol}`, { description: message });
      } finally {
        setConfirming(false);
      }
    })();
  }, [
    canSave,
    confirming,
    consentGranted,
    needsBrokerReconnectConfirm,
    onSave,
    props.brokerAccountCode,
    resolvedSymbol,
    row,
    selectedQuoteCandidate?.instrument_id,
    selection,
    selectedSource,
  ]);

  const handleSkip = useCallback(
    (reason: "unsupported" | "user_declined") => {
      if (!onSkipProp || saving || confirming) return;
      void onSkipProp(reason);
    },
    [confirming, onSkipProp, saving]
  );

  useEffect(() => {
    if (isDirty) {
      setRecentlySaved(false);
    }
  }, [isDirty]);

  const confirmHint =
    confirming || (rowSynced && showResolvingUi)
      ? "Locating symbol with your Asset class…"
      : showPreferenceSavedState
        ? `Saved for ${row.symbol}: ${savedListingSummary}. Change asset class or OHLC source to update.`
        : saveBlocker && !(needsConsent && !consentGranted)
          ? saveBlocker
          : effectiveRequireDirtyToSave && !isDirty
            ? "Change asset class or OHLC source to enable Confirm."
            : null;

  useEffect(() => {
    if (!actionsInFooter) {
      onFooterActionsChange?.(null);
      return;
    }
    onFooterActionsChange?.({
      symbol: row.symbol,
      canConfirm: primaryIsSkipOnly
        ? !confirming
        : canSave && !confirming && !showPreferenceSavedState,
      confirmLabel: primaryIsSkipOnly ? "Continue without charts" : confirmActionLabel,
      confirmHint: primaryIsSkipOnly ? null : confirmHint,
      busy: confirming || Boolean(saving) || (rowSynced && showResolvingUi),
      onConfirm: primaryIsSkipOnly
        ? () => handleSkip("unsupported")
        : handleConfirm,
      skipLabel: showSecondarySkip ? "Skip / continue without charts" : null,
      onSkip: showSecondarySkip ? () => handleSkip(secondarySkipReason) : null,
    });
  }, [
    actionsInFooter,
    canSave,
    confirmHint,
    confirming,
    handleConfirm,
    handleSkip,
    onFooterActionsChange,
    primaryIsSkipOnly,
    confirmActionLabel,
    row.symbol,
    rowSynced,
    saving,
    secondarySkipReason,
    showResolvingUi,
    showSecondarySkip,
    showPreferenceSavedState,
  ]);

  useEffect(() => {
    return () => {
      onFooterActionsChange?.(null);
    };
  }, [onFooterActionsChange]);

  return (
    <div
      data-instrument-market-data-row-editor=""
      className={cn(
        "flex h-full min-w-0 flex-col gap-4 text-card-foreground",
        props.compactHeader
          ? "w-full"
          : "rounded-[6px] border border-border bg-card p-4"
      )}
    >
      {!props.compactHeader ? (
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h4 className={cn(BROKER_ALERT_TITLE_CLASS, "m-0 text-base font-medium")}>
            {row.symbol}
          </h4>
          <p className={cn(BROKER_FIELD_DESC_CLASS, "m-0 text-xs")}>
            {row.trade_count} trades
          </p>
        </div>
      ) : null}

      {otherWarnings.map((warning) => (
        <Alert key={warning} variant="info" className={BROKER_ALERT_CLASS}>
          <BrokerAlertIcon tone="info" />
          <AlertTitle className={BROKER_ALERT_TITLE_CLASS}>
            {instrumentSetupResolutionWarningTitle(warning)}
          </AlertTitle>
          <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
            {humanizeApiUserCopy(instrumentSetupResolutionWarningBody(warning))}
          </AlertDescription>
        </Alert>
      ))}

      {needsTesseraAutoMapStage ? (
        <Alert variant="info" className={BROKER_ALERT_CLASS}>
          <BrokerAlertIcon tone="info" />
          <AlertTitle className={BROKER_ALERT_TITLE_CLASS}>
            {INSTRUMENT_AUTO_MAP_STAGE_TITLE}
          </AlertTitle>
          <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
            {INSTRUMENT_AUTO_MAP_STAGE_BODY}
          </AlertDescription>
        </Alert>
      ) : null}

      {mdUnsupported && !canAttemptAutoMap ? (
        <Alert variant="info" className={BROKER_ALERT_CLASS}>
          <BrokerAlertIcon tone="info" />
          <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
            {INSTRUMENT_MD_UNSUPPORTED_AFTER_SKIP_BODY}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex min-w-0 flex-col gap-4">
        {showQuoteVariantPicker && quoteVariantOptions.length > 0 ? (
          <Field className={cn(accountSettingsFieldTightControlClassName, "max-w-md")}>
            <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>{quoteVariantFieldLabel}</FieldLabel>
            <AccountSettingsFieldSelect
              id={`instrument-quote-variant-${row.symbol}`}
              value={effectiveResolvedSymbol.trim()}
              onValueChange={handleQuoteVariantChange}
              options={quoteVariantOptions}
              placeholder="Choose variant"
            />
          </Field>
        ) : null}

        {!hideAssetClassPicker || showSourcePicker ? (
          <div
            className={cn(
              "grid w-full min-w-0 gap-4",
              !hideAssetClassPicker && showSourcePicker
                ? "grid-cols-1 sm:grid-cols-2"
                : "grid-cols-1"
            )}
          >
            {!hideAssetClassPicker ? (
              <Field className={cn(accountSettingsFieldTightControlClassName, "min-w-0")}>
                <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Asset class</FieldLabel>
                <AccountSettingsFieldSelect
                  id={`instrument-asset-class-${row.symbol}`}
                  value={effectiveAssetClass}
                  onValueChange={(value) => setAssetClass(value.trim().toLowerCase())}
                  options={assetClassMenuOptions}
                  placeholder="Select class"
                />
              </Field>
            ) : null}

            {showSourcePicker ? (
              <Field className={cn(accountSettingsFieldTightControlClassName, "min-w-0")}>
                <FieldLabel
                  className={cn(BROKER_ALERT_TITLE_CLASS, "inline-flex items-center gap-2")}
                >
                  OHLC source
                  {ohlcVisualPending ? (
                    <Loader2
                      className="size-3.5 shrink-0 animate-spin text-muted-foreground transition-opacity duration-300"
                      aria-hidden
                    />
                  ) : null}
                </FieldLabel>
                {displaySources.length > 0 ? (
                  <div
                    className={cn(
                      "min-h-9 transition-opacity duration-300 ease-in-out",
                      ohlcVisualPending && "opacity-60"
                    )}
                  >
                    <AccountSettingsFieldSelect
                      id={`instrument-ohlc-source-${row.symbol}`}
                      value={displaySelectedIndex}
                      onValueChange={setSelectedSourceIndex}
                      options={sourceMenuOptions}
                      placeholder="Select listing"
                      disabled={ohlcVisualPending}
                    />
                  </div>
                ) : ohlcAwaitingFreshLanes ? (
                  <div className="min-h-9" aria-hidden />
                ) : showResolvingUi && rowSynced ? (
                  <div
                    className={cn(
                      BROKER_FIELD_DESC_CLASS,
                      "flex min-h-9 items-center gap-2 text-sm transition-opacity duration-300"
                    )}
                  >
                    <Loader2 className="size-3.5 shrink-0 animate-spin" aria-hidden />
                    Loading sources…
                  </div>
                ) : (
                  <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
                    {ohlcLaneNeedsRefresh
                      ? describeEmptyOhlcSourcesForQuoteVariant({
                          activeResolvedSymbol: ohlcLaneQuote.trim() || undefined,
                          quoteCandidates,
                        })
                      : "No market data sources returned for this symbol yet. Pick a quote variant or use Refresh sources."}
                  </FieldDescription>
                )}
                {selectedSourceSubtitle && !selectedSourceConnectAction && !ohlcVisualPending ? (
                  <FieldDescription
                    className={cn(BROKER_FIELD_DESC_CLASS, "mt-2 leading-5 transition-opacity duration-300 ease-in-out")}
                  >
                    {selectedSourceSubtitle}
                  </FieldDescription>
                ) : null}
              </Field>
            ) : null}
          </div>
        ) : null}

        {selectedSourceConnectAction ? (
          <MarketDataSourceConnectPrompt
            action={selectedSourceConnectAction}
            symbol={row.symbol}
            onStartReconnect={
              supportsInlineMarketDataBrokerReconnect(selectedSourceConnectAction.broker)
                ? () => setReconnectDialogOpen(true)
                : undefined
            }
            onNavigate={(href) => history.push(href)}
          />
        ) : null}

        {selectedSource && needsBrokerReconnectConfirm ? (
          <MarketDataBrokerReconnectDialog
            open={reconnectDialogOpen}
            onOpenChange={setReconnectDialogOpen}
            broker={selectedBroker}
            symbol={row.symbol}
            brokerAccountCode={props.brokerAccountCode}
            row={row}
            source={selectedSource}
            selection={selection}
            resolveContext={{
              md_listing_key:
                selectedSource?.md_listing_key ??
                activeResolve?.md_listing_key ??
                row.md_listing_key ??
                undefined,
              instrument_id: activeResolve?.instrument_id ?? row.instrument_id ?? undefined,
              asset_class: selection.assetClass,
            }}
            onFlowComplete={async () => {
              setReconnectDialogOpen(false);
              if (props.onSetupRefresh) {
                await props.onSetupRefresh();
              }
              notifyTradesUpdated({
                action: "marketDataPreference",
                accountCode: props.brokerAccountCode,
              });
              setRecentlySaved(true);
            }}
          />
        ) : null}

        {showPreferenceSavedState ? (
          <Alert
            className={cn(BROKER_ALERT_CLASS, "border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40")}
            data-instrument-md-preference-saved=""
          >
            <BrokerAlertIcon tone="success" />
            <AlertTitle className={BROKER_ALERT_TITLE_CLASS}>Market data saved</AlertTitle>
            <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
              {row.symbol} is configured for {savedListingSummary}. Charts and reports will use
              this OHLC source.
            </AlertDescription>
          </Alert>
        ) : null}

        {needsConsent ? (
          <MarketDataInstrumentConsentField
            id={`instrument-md-consent-${row.symbol}`}
            checked={consentGranted}
            onCheckedChange={setConsentGranted}
            label={resolveLayer2ConsentLabel({
              symbol: row.symbol,
              attribution: consentAttribution,
              source: selectedSource,
            })}
          />
        ) : null}

        {!actionsInFooter && rowSynced && showResolvingUi ? (
          <div
            className={cn(
              BROKER_FIELD_DESC_CLASS,
              "flex items-center gap-2 transition-opacity duration-300 ease-in-out"
            )}
          >
            <Loader2
              className="size-4 animate-spin text-[color:var(--brand-tropical-surf)]"
              aria-hidden
            />
            Resolving market data…
          </div>
        ) : !actionsInFooter && saveBlocker && !(needsConsent && !consentGranted) ? (
          <p className={cn(BROKER_FIELD_DESC_CLASS, "m-0 text-xs")}>{saveBlocker}</p>
        ) : !actionsInFooter && confirmHint && !showPreferenceSavedState ? (
          <p className={cn(BROKER_FIELD_DESC_CLASS, "m-0 text-xs")}>{confirmHint}</p>
        ) : null}

        {!actionsInFooter ? (
          <div className="flex flex-wrap items-center gap-2">
            {primaryIsSkipOnly ? (
              <Button
                type="button"
                variant="gradient"
                size="sm"
                data-font="primary"
                className="h-8 px-5 text-[0.75rem] normal-case tracking-normal"
                disabled={Boolean(props.saving)}
                onClick={() => handleSkip("unsupported")}
              >
                {props.saving ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : null}
                Continue without charts
              </Button>
            ) : !showPreferenceSavedState ? (
              <Button
                type="button"
                variant="gradient"
                size="sm"
                data-font="primary"
                className="h-8 px-5 text-[0.75rem] normal-case tracking-normal"
                disabled={!canSave || confirming}
                onClick={handleConfirm}
              >
                {props.saving || confirming ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : null}
                {confirmActionLabel}
              </Button>
            ) : null}
            {showSecondarySkip ? (
              <Button
                type="button"
                variant="outline-gray"
                size="sm"
                data-font="primary"
                data-reports-rename-button=""
                className="h-8 px-5 text-[0.75rem] normal-case tracking-normal"
                disabled={Boolean(props.saving) || confirming}
                onClick={() => handleSkip(secondarySkipReason)}
              >
                Skip / continue without charts
              </Button>
            ) : null}
          </div>
        ) : confirmHint && !showPreferenceSavedState ? (
          <p className={cn(BROKER_FIELD_DESC_CLASS, "m-0 text-xs")}>{confirmHint}</p>
        ) : (
          <p className={cn(BROKER_FIELD_DESC_CLASS, "m-0 text-xs")}>
            Use Confirm below to submit this Asset class so Tessera can try to map the symbol.
          </p>
        )}
      </div>
    </div>
  );
}
